import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Clock, Instagram, Facebook } from 'lucide-react';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';

export default function Footer() {
  const location = useLocation();
  const { selectedLocation, locations } = useLocationContext();

  // Don't show footer on admin or kiosk routes
  if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/kiosk')) {
    return null;
  }

  // Shop pages use light theme footer
  const isShop = location.pathname.startsWith('/bestellen') || location.pathname === '/meine-bestellungen';

  return (
    <footer className={`${isShop ? 'bg-[#F0EFEC] border-t border-neutral-200' : 'bg-neutral-900 border-t border-neutral-800'} mt-auto`}>
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="inline-flex items-center gap-3">
              <img src="/images/logo-icon.png" alt="ZOZO Burger" className="w-12 h-12 object-contain" />
              <div className="flex flex-col">
                <span className={`font-display text-xl font-bold tracking-wide ${isShop ? 'text-[#292524]' : 'text-white'}`}>ZOZO</span>
                <span className={`text-[9px] font-semibold tracking-[0.18em] uppercase ${isShop ? 'text-stone-400' : 'text-neutral-500'}`}>Burger · Pizza · Pasta & More</span>
              </div>
            </Link>
            <p className={`text-sm leading-relaxed ${isShop ? 'text-stone-500' : 'text-neutral-500'}`}>
              Premium Burger, Pizza & mehr. Frisch zubereitet, schnell geliefert.
            </p>
            <div className="flex gap-4">
              <a href="#" className={`transition-colors ${isShop ? 'text-stone-400 hover:text-stone-700' : 'text-neutral-400 hover:text-white'}`} aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className={`transition-colors ${isShop ? 'text-stone-400 hover:text-stone-700' : 'text-neutral-400 hover:text-white'}`} aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Locations */}
          {locations.slice(0, 3).map((loc) => (
            <div key={loc.slug} className="space-y-3">
              <h4 className={`font-display text-lg ${isShop ? 'text-[#292524]' : 'text-white'}`}>{loc.name}</h4>
              <div className={`space-y-2 text-sm ${isShop ? 'text-stone-500' : 'text-neutral-400'}`}>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-primary-500 flex-shrink-0" />
                  <span>{loc.address.street}, {loc.address.postal_code} {loc.address.city}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary-500 flex-shrink-0" />
                  <a href={`tel:${loc.phone.replace(/\s/g, '')}`} className={`transition-colors ${isShop ? 'hover:text-stone-800' : 'hover:text-white'}`}>
                    {loc.phone}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary-500 flex-shrink-0" />
                  <span>
                    {loc.is_open_now ? (
                      <span className="text-success">Jetzt geöffnet</span>
                    ) : (
                      <span className={isShop ? 'text-stone-400' : 'text-neutral-500'}>Geschlossen</span>
                    )}
                  </span>
                </div>
                {loc.google_review_url && (
                  <a href={loc.google_review_url} target="_blank" rel="noreferrer"
                    className={`flex items-center gap-2 transition-colors ${isShop ? 'text-amber-600 hover:text-amber-700' : 'text-amber-400/90 hover:text-amber-300'}`}
                    data-testid={`footer-review-link-${loc.slug}`}>
                    <span aria-hidden="true">⭐</span> Bewerte uns auf Google
                  </a>
                )}
              </div>
            </div>
          ))}

          {/* Pinneberg (Eröffnet bald) */}
          {!locations.some((l) => l.slug === 'pinneberg') && (
            <div className="space-y-3" data-testid="footer-pinneberg">
              <h4 className={`font-display text-lg ${isShop ? 'text-[#292524]' : 'text-white'}`}>ZOZO Burger Pinneberg</h4>
              <div className={`space-y-2 text-sm ${isShop ? 'text-stone-500' : 'text-neutral-400'}`}>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-primary-500 flex-shrink-0" />
                  <span>Berliner Straße 51, 25421 Pinneberg</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary-500 flex-shrink-0" />
                  <span className="text-amber-500 font-medium">Eröffnet bald</span>
                </div>
              </div>
            </div>
          )}

          {/* Links */}
          <div className="space-y-3">
            <h4 className={`font-display text-lg ${isShop ? 'text-[#292524]' : 'text-white'}`}>Links</h4>
            <div className="space-y-2 text-sm">
              <Link to="/franchise" className={`block transition-colors ${isShop ? 'text-stone-500 hover:text-stone-800' : 'text-neutral-400 hover:text-white'}`}>
                Franchise
              </Link>
              <Link to="/impressum" className={`block transition-colors ${isShop ? 'text-stone-500 hover:text-stone-800' : 'text-neutral-400 hover:text-white'}`}>
                Impressum
              </Link>
              <Link to="/datenschutz" className={`block transition-colors ${isShop ? 'text-stone-500 hover:text-stone-800' : 'text-neutral-400 hover:text-white'}`}>
                Datenschutz
              </Link>
              <Link to="/agb" className={`block transition-colors ${isShop ? 'text-stone-500 hover:text-stone-800' : 'text-neutral-400 hover:text-white'}`}>
                AGB
              </Link>
              <Link to="/kontakt" className={`block transition-colors ${isShop ? 'text-stone-500 hover:text-stone-800' : 'text-neutral-400 hover:text-white'}`}>
                Kontakt
              </Link>
              <Link to="/allergene" className={`block transition-colors ${isShop ? 'text-stone-500 hover:text-stone-800' : 'text-neutral-400 hover:text-white'}`} data-testid="footer-allergene-link">
                Allergene &amp; Zusatzstoffe
              </Link>
              <Link to="/freunde-werben" className={`block transition-colors ${isShop ? 'text-stone-500 hover:text-stone-800' : 'text-neutral-400 hover:text-white'}`} data-testid="footer-freunde-werben-link">
                Freunde werben · 5&nbsp;€
              </Link>
              <Link to="/meine-bestellungen" className={`block transition-colors ${isShop ? 'text-stone-500 hover:text-stone-800' : 'text-neutral-400 hover:text-white'}`} data-testid="footer-my-orders">
                Meine Bestellungen
              </Link>
              <a
                href="/api/email/unsubscribe-manual"
                className={`block transition-colors ${isShop ? 'text-stone-500 hover:text-stone-800' : 'text-neutral-400 hover:text-white'}`}
                data-testid="footer-newsletter-unsubscribe"
              >
                Newsletter abmelden
              </a>
            </div>
          </div>
        </div>

        <div className={`mt-12 pt-8 border-t text-center text-sm ${isShop ? 'border-neutral-200 text-stone-400' : 'border-neutral-800 text-neutral-500'}`}>
          <p>© {new Date().getFullYear()} Zozo Burger. Alle Rechte vorbehalten.</p>
          <p className="mt-2 text-xs opacity-70" data-testid="ai-disclosure-note">Einzelne Bilder und Videos auf dieser Website wurden mit Unterstützung von KI erstellt.</p>
        </div>
      </div>
    </footer>
  );
}
